import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  Phone, Mail, MessageSquare, Calendar, FileText, 
  Linkedin, MessageCircle, Send, Eye, MousePointer,
  Reply, Video, Users
} from 'lucide-react';

/**
 * Social Activity Timeline Component
 * Shows unified timeline of all touchpoints: LinkedIn, WhatsApp, Email, Calls, etc.
 */

const ACTIVITY_CONFIG = {
  // Traditional activities
  call: { icon: Phone, color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400', label: 'Call' },
  email: { icon: Mail, color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400', label: 'Email Sent' },
  meeting: { icon: Calendar, color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400', label: 'Meeting' },
  follow_up: { icon: FileText, color: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400', label: 'Follow-up' },
  note: { icon: FileText, color: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400', label: 'Note' },
  demo: { icon: Video, color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400', label: 'Demo' },
  proposal: { icon: FileText, color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400', label: 'Proposal' },
  negotiation: { icon: Users, color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400', label: 'Negotiation' },
  
  // LinkedIn activities
  linkedin_view: { icon: Linkedin, color: 'bg-[#0A66C2]/10 text-[#0A66C2]', label: 'Viewed LinkedIn Profile' },
  linkedin_connect: { icon: Linkedin, color: 'bg-[#0A66C2]/10 text-[#0A66C2]', label: 'Connection Request Sent' },
  linkedin_message: { icon: Linkedin, color: 'bg-[#0A66C2]/10 text-[#0A66C2]', label: 'LinkedIn Message' },
  linkedin_engage: { icon: Linkedin, color: 'bg-[#0A66C2]/10 text-[#0A66C2]', label: 'LinkedIn Engagement' },
  
  // WhatsApp activities
  whatsapp_message: { icon: MessageCircle, color: 'bg-[#25D366]/10 text-[#25D366]', label: 'WhatsApp Message' },
  whatsapp_call: { icon: Phone, color: 'bg-[#25D366]/10 text-[#25D366]', label: 'WhatsApp Call' },
  
  // SMS
  sms: { icon: MessageSquare, color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400', label: 'SMS Sent' },
  
  // Email tracking events
  email_opened: { icon: Eye, color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400', label: 'Email Opened' },
  email_clicked: { icon: MousePointer, color: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400', label: 'Link Clicked' },
  email_replied: { icon: Reply, color: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400', label: 'Reply Received' },
};

const PLATFORM_FILTERS = [
  { id: 'all', label: 'All Activities' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'phone', label: 'Phone', icon: Phone },
];

export function SocialTimeline({ leadId, compact = false, maxItems = 10 }) {
  const [activities, setActivities] = useState([]);
  const [emailTracking, setEmailTracking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (leadId) {
      fetchActivities();
    }
  }, [leadId]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      // Fetch regular activities
      const activitiesRes = await axios.get(`${API}/activities/lead/${leadId}`);
      
      // Fetch email communications with tracking data
      const commsRes = await axios.get(`${API}/communications`, {
        params: { lead_id: leadId }
      }).catch(() => ({ data: [] }));

      // Process email tracking events into activities
      const emailEvents = [];
      commsRes.data?.forEach(comm => {
        // Add email sent activity
        emailEvents.push({
          id: `${comm.id}_sent`,
          activity_type: 'email',
          notes: comm.subject || 'Email sent',
          created_at: comm.sent_at,
          user_name: comm.user_name || 'You',
          platform: 'email'
        });

        // Add email opened events
        if (comm.opens?.length > 0) {
          comm.opens.forEach((open, idx) => {
            emailEvents.push({
              id: `${comm.id}_open_${idx}`,
              activity_type: 'email_opened',
              notes: `Opened: ${comm.subject || 'Email'}`,
              created_at: open.opened_at,
              user_name: 'Lead',
              platform: 'email',
              metadata: { user_agent: open.user_agent }
            });
          });
        }

        // Add link click events
        if (comm.clicks?.length > 0) {
          comm.clicks.forEach((click, idx) => {
            emailEvents.push({
              id: `${comm.id}_click_${idx}`,
              activity_type: 'email_clicked',
              notes: `Clicked link in: ${comm.subject || 'Email'}`,
              created_at: click.clicked_at,
              user_name: 'Lead',
              platform: 'email',
              metadata: { url: click.url }
            });
          });
        }
      });

      // Combine and sort all activities
      const allActivities = [...activitiesRes.data, ...emailEvents]
        .map(a => ({
          ...a,
          platform: a.platform || getPlatformFromType(a.activity_type)
        }))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setActivities(allActivities);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformFromType = (type) => {
    if (type?.startsWith('linkedin')) return 'linkedin';
    if (type?.startsWith('whatsapp')) return 'whatsapp';
    if (type?.startsWith('email') || type === 'email') return 'email';
    if (type === 'call' || type === 'sms') return 'phone';
    return 'other';
  };

  const filteredActivities = activities.filter(a => {
    if (filter === 'all') return true;
    return a.platform === filter;
  });

  const displayActivities = compact 
    ? filteredActivities.slice(0, maxItems) 
    : filteredActivities;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-slate-200 dark:border-slate-700 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="social-timeline">
      {/* Filter Tabs */}
      {!compact && (
        <div className="flex flex-wrap gap-2 pb-3 border-b border-slate-200 dark:border-slate-700">
          {PLATFORM_FILTERS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setFilter(id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                filter === id
                  ? 'bg-slate-900 dark:bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
              }`}
              data-testid={`filter-${id}`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Timeline */}
      {displayActivities.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
            <FileText className="w-6 h-6 text-slate-400 dark:text-slate-500" />
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">No activities yet</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700"></div>

          <div className="space-y-4">
            {displayActivities.map((activity) => {
              const config = ACTIVITY_CONFIG[activity.activity_type] || ACTIVITY_CONFIG.note;
              const Icon = config.icon;

              return (
                <div key={activity.id} className="relative flex gap-4" data-testid={`activity-${activity.id}`}>
                  {/* Icon */}
                  <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full ${config.color} flex items-center justify-center`}>
                    <Icon className="w-4 h-4" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white text-sm">{config.label}</p>
                        {activity.notes && (
                          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{activity.notes}</p>
                        )}
                        {activity.metadata?.url && (
                          <a 
                            href={activity.metadata.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1 block truncate max-w-[250px]"
                          >
                            {activity.metadata.url}
                          </a>
                        )}
                      </div>
                      <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                        {activity.created_at 
                          ? formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })
                          : '-'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
                      <span>by {activity.user_name || 'Unknown'}</span>
                      {activity.duration_minutes && (
                        <span>• {activity.duration_minutes} min</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Show More */}
      {compact && filteredActivities.length > maxItems && (
        <button className="w-full py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
          View all {filteredActivities.length} activities →
        </button>
      )}
    </div>
  );
}

/**
 * LinkedIn Quick Actions Component
 * Provides quick buttons to view profile, log activities, etc.
 */
export function LinkedInActions({ linkedinUrl, leadId, leadName, onActivityLogged }) {
  const [logging, setLogging] = useState(false);

  const logLinkedInActivity = async (activityType, notes) => {
    setLogging(true);
    try {
      await axios.post(`${API}/activities`, {
        lead_id: leadId,
        activity_type: activityType,
        notes: notes || `${activityType.replace('linkedin_', '').replace('_', ' ')} for ${leadName}`
      });
      onActivityLogged?.();
    } catch (error) {
      console.error('Error logging activity:', error);
    } finally {
      setLogging(false);
    }
  };

  if (!linkedinUrl) {
    return null;
  }

  // Normalize LinkedIn URL
  const normalizedUrl = linkedinUrl.startsWith('http') 
    ? linkedinUrl 
    : `https://www.linkedin.com/in/${linkedinUrl}`;

  return (
    <div className="flex items-center gap-2" data-testid="linkedin-actions">
      {/* View Profile Button */}
      <a
        href={normalizedUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => logLinkedInActivity('linkedin_view', `Viewed LinkedIn profile`)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0A66C2] text-white text-xs font-medium rounded-lg hover:bg-[#004182] transition-colors"
        data-testid="linkedin-view-btn"
      >
        <Linkedin className="w-3.5 h-3.5" />
        View Profile
      </a>

      {/* Quick Log Dropdown */}
      <div className="relative group">
        <button 
          className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-[#0A66C2] hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          disabled={logging}
        >
          <Send className="w-4 h-4" />
        </button>
        <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
          <button
            onClick={() => logLinkedInActivity('linkedin_connect', 'Sent connection request')}
            className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
          >
            <Users className="w-4 h-4 text-slate-400 dark:text-slate-500" />
            Log Connection Request
          </button>
          <button
            onClick={() => logLinkedInActivity('linkedin_message', 'Sent LinkedIn message')}
            className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
          >
            <MessageSquare className="w-4 h-4 text-slate-400 dark:text-slate-500" />
            Log Message Sent
          </button>
          <button
            onClick={() => logLinkedInActivity('linkedin_engage', 'Engaged with LinkedIn post')}
            className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
          >
            <FileText className="w-4 h-4 text-slate-400 dark:text-slate-500" />
            Log Engagement
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Email Tracking Badge Component
 * Shows email open/click status with counts
 */
export function EmailTrackingBadge({ status, openCount, clickCount }) {
  if (!status || status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-xs rounded-full">
        <Send className="w-3 h-3" />
        Sent
      </span>
    );
  }

  if (status === 'opened' || openCount > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
        <Eye className="w-3 h-3" />
        Opened {openCount > 1 ? `(${openCount}x)` : ''}
      </span>
    );
  }

  if (status === 'clicked' || clickCount > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 text-xs rounded-full">
        <MousePointer className="w-3 h-3" />
        Clicked {clickCount > 1 ? `(${clickCount}x)` : ''}
      </span>
    );
  }

  return null;
}

export default SocialTimeline;
